import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";
import { AuthContext, IAuthState } from "./AuthContext";
import { useSelf } from "../hooks/useSelf";
import { useQueryClient } from "@tanstack/react-query";

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const [authState, setAuthState] = useState<IAuthState>({
    isLogged: false,
    isLoading: true,
    isAdmin: null,
  });

  const [forceUserView, setForceUserView] = useState(false);

  const queryClient = useQueryClient();
  const self = useSelf();

  const toggleUserView = useCallback(() => {
    if (self.self?.is_synota_admin) {
      setForceUserView((current) => !current);
    }
  }, [self]);

  useEffect(() => {
    if (self.status === "success") {
      setAuthState((curr) => ({
        ...curr,
        isLogged: true,
        isAdmin: self.self?.is_synota_admin || null,
      }));
    }

    if (self.error && self.error.status === 401) {
      setForceUserView(false);
      setAuthState((curr) => {
        if (curr.isLogged) {
          queryClient.removeQueries();
        }
        return { ...curr, isLogged: false, isAdmin: null };
      });
    }

    setAuthState((curr) => ({ ...curr, isLoading: self.isLoading }));
  }, [queryClient, self.error, self.isLoading, self.self, self.status]);

  const value = useMemo(
    () => ({
      ...authState,
      isAdmin: forceUserView ? false : authState.isAdmin,
      self: self.self,
      toggleUserView,
      refetch: self.refetch,
    }),
    [authState, forceUserView, self.refetch, self.self, toggleUserView],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
